import {OmyConst} from "../../OmyConst";
import Omy from "../../Omy";
import OContainer from "../OContainer";

/**
 * docs https://neutrinoparticles.gitlab.io/neutrinoparticles.pixi.js-doc/master/
 */
export default class ONeutrinoParticles extends OContainer {
    constructor(json) {
        super();

        this._oType = OmyConst.OMY_NEUTRINO_PARTICLES;
        this.json = json;
        this._onCompleteEvent = "oncomplete";
        this._onStartPlayEvent = "startplay";

        this._isBaseParent = Boolean(this.json["baseParent"]) || false;
        this._removeOnEnd = Boolean(this.json["removeOnEnd"]) || false;
        this._killOnEnd = Boolean(this.json["killOnEnd"]) || false;
        this._loop = Boolean(this.json["loop"]) || false;
        this._effects = [];
        this.addEmitter(this.json["config"]);
        this._paused = true;
        this._pausedGenerators = false;

        this.interactChild = false;
        this.isUpdate = true;
    }

    /** @public
     * @param {Object}emitter
     * @return Number
     */
    addEmitter(emitter) {
        let effect = new PIXINeutrino.Effect(Omy.resources[emitter["particle_name"]].effectModel, emitter);
        effect.particleName = emitter["particle_name"];
        if (this._isBaseParent) effect.baseParent = this;
        this._effects.push(effect);
        this.addChild(effect);
        return this._effects.length - 1;
    }

    /** @public
     * Unpauses the effect.
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * */
    play(emitterName = null, name = null) {
        this._timerDelayPauseGenerators?.destroy();
        this._timerDelayPauseGenerators = null;
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                (this._effects[i].effect["_" + emitterName] || this._effects[i]).unpause();
        }
    }

    /** @public
     * Pauses the effect. All active particles will freeze.
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * */
    stop(emitterName = null, name = null) {
        this._timerDelayPauseGenerators?.destroy();
        this._timerDelayPauseGenerators = null;
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                (this._effects[i].effect["_" + emitterName] || this._effects[i]).pause();
        }
    }

    /** @public
     * Unpauses generators in the effect.
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * */
    unpauseGenerators(emitterName = null, name = null) {
        if (!this._pausedGenerators) return;
        this._pausedGenerators = false;
        this._timerDelayPauseGenerators?.destroy();
        this._timerDelayPauseGenerators = null;
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                (this._effects[i].effect["_" + emitterName] || this._effects[i]).unpauseGenerators();
        }
    }

    /** @public
     * Pauses all generators in the effect. No new particles will be generated. All current particles will continue to update.
     * @param {String}emitterName=null, Name of effects emitter.
     * @param {String}name=null, Name of effect.
     * @param {Number}delay=0, Pause with delay in sec.
     * */
    pauseGenerators(emitterName = null, name = null, delay = 0) {
        if (this._pausedGenerators) return;
        this._pausedGenerators = true;
        this._timerDelayPauseGenerators?.destroy();
        this._timerDelayPauseGenerators = null;
        if (delay > 0) {
            this._timerDelayPauseGenerators = Omy.add.timer(delay, this.pauseGenerators.bind(this, emitterName, name), this);
            return;
        }
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                (this._effects[i].effect["_" + emitterName] || this._effects[i]).pauseGenerators();
        }
    }

    /** @public
     * Activate effect or emitter
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * */
    activate(emitterName, name = null) {
        if (!emitterName?.length) {
            OMY.Omy.error("pls set name of emitter", name, emitterName);
            return null;
        }
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName) && this._effects[i].effect["_" + emitterName])
                this._effects[i].effect["_" + emitterName].activate();
        }
    }

    /** @public
     * Deactivate effect or emitter
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * */
    disactivate(emitterName, name = null) {
        if (!emitterName?.length) {
            OMY.Omy.error("pls set name of emitter", name, emitterName);
            return null;
        }
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName) && this._effects[i].effect["_" + emitterName])
                this._effects[i].effect["_" + emitterName].disactivate();
        }
    }

    /** @public
     * Restarts the effect.
     * @param {Array}position, A new position of the effect or null to keep the same position.
     * @param {Array}rotation, A new rotation of the effect of null to keep the same rotation.
     * @param {String}name, Name of effect.
     */
    restart(position = null, rotation = null, name = null) {
        this._timerDelayPauseGenerators?.destroy();
        this._timerDelayPauseGenerators = null;
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                this._effects[i].restart(position, rotation);
        }
    }

    /** @public
     * Instant change of position/rotation of the effect. Teleport
     * @param {Array}position, A new position of the effect or null to keep the same position.
     * @param {Array}rotation, A new rotation of the effect of null to keep the same rotation.
     * @param {String}name, Name of effect.
     */
    resetPosition(position = null, rotation = null, name = null) {
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                this._effects[i].resetPosition(position, rotation);
        }
    }

    /** @public
     * Restart all effects and stop
     * @param {String}name, Name of effect.
     */
    reset(name = null) {
        this.restart(name);
        this.stop(name);
    }

    /**     * @public     */
    kill() {
        if (this._active) {
            this.stop();
            super.kill();
        }

        return this;
    }

    /**     * @public     */
    revive() {
        if (!this._active) {
            super.revive();
            this.restart();
        }

        return this;
    }

    /**     * @public     */
    destroy(options) {
        if (this.destroyed) return this;
        this.stop();
        this._effects.length = 0;
        this._effects = null;
        this._oType = null;
        this.json = null;

        super.destroy(options);
        return this;
    }

    /**     * @public     */
    update(delta) {
        const msec = delta / PIXI.settings.TARGET_FPMS;
        const sec = msec / 1000.0;
        for (let i = 0; i < this._effects.length; i++) {
            this._effects[i].update(sec);
        }
        super.update(delta);

        if (this.numActiveParticles < 1 && !this._paused) {
            this._paused = true;
            this.emit(this._onCompleteEvent);
            if (this._removeOnEnd)
                this.destroy();
            else if (this._killOnEnd)
                this.kill();
            else if (this._loop)
                this.restart();
        } else if (this.numActiveParticles > 0 && this._paused) {
            this._paused = false;
            this.emit(this._onStartPlayEvent);
        }

    }

    zeroUpdate() {
        for (let i = 0; i < this._effects.length; i++) {
            this._effects[i].effect.zeroUpdate();
        }
    }

    /**
     * @param {Number}id
     * @returns {null|PIXINeutrino.Effect}
     */
    getEffect(id = 0) {
        return this._effects[id];
    }

    /**
     * @param {String}name
     * @returns {null|PIXINeutrino.Effect}
     */
    getEffectByName(name = "") {
        for (let i = 0; i < this._effects.length; i++) {
            if (this._effects[i].particleName === name) return this._effects[i];
        }
        return null;
    }

    /** @public
     * Get emitter from effect.
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     * @returns {object}
     */
    getEmitterByName(emitterName = "", name = null) {
        if (!emitterName?.length) {
            OMY.Omy.error("pls set name of emitter", name, emitterName);
            return null;
        }
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName) && this._effects[i].effect[emitterName])
                return this._effects[i].effect[emitterName];
        }
        return null;
    }

    /** @public
     * Sets a value of emitter property in all emitters of the effect.
     * @param {String}nameParam, Name of property.
     * @param {Number|Array}value, Value of property. It can be a Number, 2-, 3- or 4-component Array depending on a property you want to set.
     * @param {String}emitterName, Name of effects emitter.
     * @param {String}name, Name of effect.
     */
    setPropertyEmitters(nameParam, value, emitterName = null, name = null) {
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName)) {
                if (this._effects[i].effect["_" + emitterName])
                    this._effects[i].effect["_" + emitterName]["_" + nameParam] = value;
                else
                    this._effects[i].setPropertyInAllEmitters(nameParam, value);
            }
        }
    }

    /** @public
     * Calc num of particles by names params. All null - all particles on all effects.
     * @param {String}name, Name of effect.
     * @param {String}emitterName, Name of effects emitter.
     * @returns {number}
     */
    getNumParticles(name = null, emitterName = null) {
        let result = 0;
        for (let i = 0; i < this._effects.length; i++) {
            if ((!name || name === this._effects[i].particleName))
                result += (this._effects[i].effect["_" + emitterName] || this._effects[i].effect).getNumParticles();
        }
        return result;
    }

    /** @public
     * Get, are this np effect (or someone) playing now.
     * @param {String}name, Name of effect.
     * @returns {boolean}
     */
    playing(name = null) {
        for (let i = 0; i < this._effects.length; i++) {
            if (!(this._effects[i].effect.areAllEmittersPaused() &&
                this._effects[i].effect.areGeneratorsInAllEmittersPaused()))
                return true;
        }
        return false;
    }

    /**@public
     * Add listener for event onComplete
     * @param {function} func - add method for listener
     * @param {*} context - add display object who have listener
     * @param {boolean} [once=false]once - emit only one time
     * @return {null}
     */
    addComplete(func, context, once = false) {
        this._addNewEvent(this._onCompleteEvent, func, context, once);
    }

    /**@public
     * remove listener for event onComplete
     * @param {function} func - add method for listener
     * @param {*} context - add display object who have listener
     * @return {null}
     */
    removeComplete(func, context) {
        this.removeListener(this._onCompleteEvent, func, context);
    }

    /**@public
     * Add listener for event startPlay
     * @param {function} func - add method for listener
     * @param {*} context - add display object who have listener
     * @param {boolean} [once=false]once - emit only one time
     * @return {null}
     */
    addStartPlay(func, context, once = false) {
        this._addNewEvent(this._onStartPlayEvent, func, context, once);
    }

    /**@public
     * Remove listener for event startPlay
     * @param {function} func - add method for listener
     * @param {*} context - add display object who have listener
     * @return {null}
     */
    removeStartPlay(func, context) {
        this.removeListener(this._onStartPlayEvent, func, context);
    }

//---------------------------------------
/// ACCESSOR
//--------------------------------------
    get numEffects() {
        return this._effects.length;
    }

    set removeOnEnd(value) {
        this._removeOnEnd = value;
    }

    get removeOnEnd() {
        return this._removeOnEnd;
    }

    set killOnEnd(value) {
        this._killOnEnd = value;
    }

    get killOnEnd() {
        return this._killOnEnd;
    }

    /** @public
     * Calc num of particles.
     * @returns {number}
     */
    get numActiveParticles() {
        let result = 0;
        for (let i = 0; i < this._effects.length; i++) {
            result += this._effects[i].effect.getNumParticles();
        }
        return result;
    }

    get loop() {
        return this._loop;
    }

    set loop(value) {
        this._loop = value;
    }

    get pausedGenerators() {
        return this._pausedGenerators;
    }
}
